import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { qs } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { brandColor, basisLabel, fmt, pct, signed, downloadCSV } from "@/lib/utils-msip";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

export default function BrandWars() {
  const { filters, meta } = useApp();
  const [a, setA] = useState(filters.brand);
  const [b, setB] = useState(null);
  const [data, setData] = useState(null);

  const brandList = (meta?.brands || []).map((x) => x.name).filter((x) => !(filters.basis === "exm" && x === "MARUTI"));
  const rival = (meta?.brands || []).find((x) => x.name === a)?.rival || "HYUNDAI";
  const brandB = b || rival;

  useEffect(() => setA(filters.brand), [filters.brand]);

  useEffect(() => {
    if (!a || !brandB) return;
    api
      .get(`/brandwars?${qs({ brand_a: a, brand_b: brandB, basis: filters.basis, period: filters.period === "daily" ? "mtd" : filters.period, district: filters.district, territory_only: filters.territoryOnly })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [a, brandB, filters.basis, filters.period, filters.district, filters.territoryOnly]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading…</div>;

  const exportMap = () =>
    downloadCSV(
      `brandwars_${a}_vs_${brandB}.csv`,
      ["RTO", "District", "TIV", `${a} %`, `${brandB} %`, "Leader", "Margin"],
      data.rto_rows.map((r) => [r.rto, r.district, r.tiv, r.a_share, r.b_share, r.leader, r.margin]),
      filters.basis
    );

  const Chip = ({ name }) => (
    <span className="rounded px-1.5 py-0.5 text-[11px] font-bold text-white" style={{ background: name === "tie" ? "#94a3b8" : brandColor(name) }}>{name === "tie" ? "TIE" : name}</span>
  );

  return (
    <div className="space-y-5" data-testid="brand-wars">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">Brand Wars</h1>
        <Select value={a} onValueChange={setA}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="brandwars-a-select"><SelectValue /></SelectTrigger>
          <SelectContent>{brandList.map((x) => <SelectItem key={x} value={x} disabled={x === brandB}>{x}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm font-bold text-muted-foreground">vs</span>
        <Select value={brandB} onValueChange={setB}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="brandwars-b-select"><SelectValue /></SelectTrigger>
          <SelectContent>{brandList.map((x) => <SelectItem key={x} value={x} disabled={x === a}>{x}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{data.period?.label} · {basisLabel(filters.basis)}</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={exportMap} data-testid="brandwars-export-csv-btn"><Download size={14} className="mr-1" /> CSV</Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[[a, data.led[a]], [brandB, data.led[brandB]], ["tie", data.led.tie]].map(([name, count]) => (
          <div key={name} className="border bg-card rounded-md p-4 text-center" data-testid={`led-count-${name}`}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{name === "tie" ? "Tied" : `${name} leads`}</div>
            <div className="num text-3xl font-extrabold" style={{ color: name === "tie" ? undefined : brandColor(name) }}>{count}</div>
            <div className="text-xs text-muted-foreground">markets</div>
          </div>
        ))}
      </div>

      <div className="border bg-card rounded-md p-4" data-testid="brandwars-trend">
        <h3 className="font-heading text-sm font-semibold mb-3">Share trend — {a} vs {brandB}</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.trend} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} unit="%" />
            <Tooltip formatter={(v) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey={a} stroke={brandColor(a)} strokeWidth={2.5} dot={{ r: 2.5 }} />
            <Line type="monotone" dataKey={brandB} stroke={brandColor(brandB)} strokeWidth={2.5} dot={{ r: 2.5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border bg-card rounded-md overflow-auto max-h-[60vh]" data-testid="winloss-table">
          <div className="p-4 pb-2"><h3 className="font-heading text-sm font-semibold">RTO-by-RTO win/loss</h3></div>
          <table className="w-full text-sm">
            <thead className="border-b sticky top-0 bg-card">
              <tr>
                {["RTO", "TIV", `${a} %`, `${brandB} %`, "Margin", "Leader"].map((h, i) => (
                  <th key={h} className={`px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground ${i > 0 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rto_rows.map((r) => (
                <tr key={r.rto} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-3 py-1.5 font-medium whitespace-nowrap">
                    <Link to={`/rto/${encodeURIComponent(r.rto)}`} className="hover:underline">{r.rto}</Link>
                    {r.is_state_row && <span className="ml-1 text-[10px] rounded bg-secondary px-1 text-muted-foreground">STATE</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right num">{fmt(r.tiv)}</td>
                  <td className="px-3 py-1.5 text-right num">{pct(r.a_share)}</td>
                  <td className="px-3 py-1.5 text-right num">{pct(r.b_share)}</td>
                  <td className={`px-3 py-1.5 text-right num ${r.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>{signed(r.margin)}</td>
                  <td className="px-3 py-1.5 text-right"><Chip name={r.leader} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-4">
          {[["By district", data.by_district, "district"], ["By month (last 6)", data.by_month, "month"]].map(([title, rows, key]) => (
            <div key={key} className="border bg-card rounded-md overflow-auto" data-testid={`competitive-${key}`}>
              <div className="p-4 pb-2"><h3 className="font-heading text-sm font-semibold">Competitive table — {title}</h3></div>
              <table className="w-full text-xs">
                <thead className="border-b">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{key}</th>
                    <th className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">TIV</th>
                    {data.brands.map((br) => (
                      <th key={br} className="px-2 py-2 text-right text-[9px] font-bold whitespace-nowrap" style={{ color: brandColor(br) }}>{br === "OTHERS" ? "OTH" : br.length > 5 ? br.slice(0, 4) : br}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r[key]} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-2 py-1.5 font-medium whitespace-nowrap">{r[key]}</td>
                      <td className="px-2 py-1.5 text-right num">{fmt(r.tiv)}</td>
                      {data.brands.map((br) => (
                        <td key={br} className="px-2 py-1.5 text-right num">{r[br]?.toFixed(1)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
